import { isAxiosError } from 'axios';
import { t } from 'i18next';

import { SkyviewReportUpdatedMessage } from '../../../../../../../../typings/websocket-messages';
import { TokenService } from '../../../../../../services/authentication/token.service';
import { SkyMapAxiosServiceFactory } from '../../../../../../services/axios/skymap-axios-service-factory';
import { FileUpload } from '../../../../../../services/network/file-upload';
import { WebApi } from '../../../../../../services/web-api';
import { WebsocketManager } from '../../../../../../utils/websocket/websocket-manager';
import {
  publish,
  subscribe,
  SubscriptionTopic,
  unsubscribe,
} from '../../../../../../viewer/messaging/pubsub';
import { LegacyDrawing } from '../../../../../../viewer/report/collect-report-layers';
import { OmittedLayer } from '../../../../../../viewer/report/manifest';
import { prepareDrawings } from '../../../../../../viewer/report/prepare-drawings';
import { downloadBlob } from '../helpers/utils';
import { ReportPayload, zipReport } from './report-zip';

export type { ReportFiles, ReportPayload } from './report-zip';

/** What the report is made of, built once the drawings are converted. */
export type ReportContent = {
  /** The drawings shown from their legacy DXF, whose versions are converted to glb first. */
  drawings: LegacyDrawing[];
  /** Builds the zip's content, leaving out the versions that couldn't be converted. */
  buildPayload: (unconvertedVersionIds: ReadonlySet<string>) => Promise<ReportPayload>;
};

export type ReportRequest = {
  projectId: string;
  /** The name of the PDF, ending in .pdf. */
  fileName: string;
  saveResult: boolean;
};

/** Why an export failed: the backend's reasons, or that it couldn't be uploaded or never finished. */
export type ReportExportError =
  | Extract<SkyviewReportUpdatedMessage, { status: 'FAILED' }>['error']
  | 'upload'
  | 'timeout';

export type ReportExportState =
  | { status: 'idle' }
  /** The legacy drawings are converted. */
  | { status: 'preparing' }
  /** What is shown is read. The scene mustn't change until it is done. */
  | { status: 'collecting' }
  /** Progress is 0 to 1. */
  | { status: 'uploading'; progress: number }
  | { status: 'rendering'; progress: number }
  | {
      status: 'completed';
      fileName: string;
      downloadUrl: string;
      resultSaved: boolean;
      thumbnailUrl?: string;
      thumbnailSkipped?: 'too-large' | 'failed';
      /** What was shown but left out of the report. */
      omitted: OmittedLayer[];
    }
  | { status: 'failed'; error: ReportExportError };

/** Whether the export reads the scene, which then mustn't change. */
export const isCollectingScene = (state: ReportExportState) =>
  state.status === 'preparing' || state.status === 'collecting';

export const isExportRunning = (state: ReportExportState) =>
  isCollectingScene(state) || state.status === 'uploading' || state.status === 'rendering';

/** The report lambda's time limit: no message after this means it stopped without telling. */
const TIMEOUT_MS = 15 * 60 * 1000;

async function uploadReport(zipped: Uint8Array, onProgress: (progress: number) => void) {
  const upload = new FileUpload({
    apiBaseUrl: WebApi.getUrl(),
    authToken: new TokenService().get(),
    file: new File([zipped], 'report.zip', { type: 'application/zip' }),
    concurrency: 4,
  });
  upload.onProgress((uploaded, total) => onProgress(total > 0 ? uploaded / total : 0));

  return (await upload.start()).id;
}

/**
 * Exported as a collection to allow for mocking.
 */
export const reportExportHelpers = { prepareDrawings, zipReport, uploadReport };

/**
 * Builds the report zip as an export does, and saves it on the user's computer instead of sending
 * it, so a problem report can be replayed in the backend's tests.
 */
export async function downloadReportPayload(content: ReportContent) {
  const unconverted = await reportExportHelpers.prepareDrawings(content.drawings);
  const zipped = await reportExportHelpers.zipReport(await content.buildPayload(unconverted));
  downloadBlob(new Blob([zipped]), 'report', 'zip', undefined, 'application/zip');
}

/**
 * The CAD drawing report being exported: uploaded, then rendered by the backend, which tells its
 * progress and result over the websocket. It lives outside the report panel, so the export goes on
 * when the panel is closed.
 */
class CadDrawingReportExport {
  private state: ReportExportState = { status: 'idle' };
  private readonly listeners = new Set<() => void>();
  /** The file names of the exports, for their notifications. */
  private readonly fileNames = new Map<string, string>();
  private stopWaiting?: () => void;
  /** What the export being rendered left out. */
  private omitted: OmittedLayer[] = [];
  /** Whether the user stopped following the export, and is told in the notification center. */
  private detached = false;
  /** Whether the notification center shows the export, which it does once the backend has it. */
  private notified = false;

  getState = () => this.state;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  isRunning() {
    return isExportRunning(this.state);
  }

  /** The PDF's file name of an export started here. */
  getFileName(exportId: string) {
    return this.fileNames.get(exportId);
  }

  /**
   * Stops showing the export: the user is told in the notification center when it is done, or with
   * a message if it fails before the backend has it.
   */
  detach() {
    if (this.isRunning()) {
      this.detached = true;
    }
  }

  /** Forgets the last export's result. */
  reset() {
    if (!this.isRunning()) {
      this.setState({ status: 'idle' });
    }
  }

  async start(content: ReportContent, request: ReportRequest) {
    if (this.isRunning()) {
      return;
    }

    this.detached = false;
    this.notified = false;

    let payload: ReportPayload;
    try {
      this.setState({ status: content.drawings.length > 0 ? 'preparing' : 'collecting' });
      const unconverted = await reportExportHelpers.prepareDrawings(content.drawings);
      if (this.state.status === 'preparing') {
        this.setState({ status: 'collecting' });
      }
      payload = await content.buildPayload(unconverted);
      this.omitted = payload.manifest.state.omitted ?? [];
    } catch (error) {
      console.error('Failed to collect the report:', error);
      this.setState({ status: 'failed', error: 'internal' });
      return;
    }

    this.setState({ status: 'uploading', progress: 0 });

    let uploadId: string;
    try {
      const zipped = await reportExportHelpers.zipReport(payload);
      uploadId = await reportExportHelpers.uploadReport(zipped, (progress) =>
        this.setState({ status: 'uploading', progress }),
      );
    } catch (error) {
      console.error('Failed to upload the report:', error);
      this.setState({ status: 'failed', error: 'upload' });
      return;
    }

    // The backend may answer over the websocket before the request returns, so it listens first.
    let exportId: string | undefined;
    const early: SkyviewReportUpdatedMessage[] = [];
    const subscription = subscribe(SubscriptionTopic.WebsocketMessageRecieved, (message) => {
      if (message?.message !== 'SkyviewReportUpdated') {
        return;
      }

      if (exportId === undefined) {
        early.push(message);
      } else if (message.exportId === exportId) {
        this.onMessage(message, request.fileName);
      }
    });
    const timeout = setTimeout(() => {
      this.stopWaiting?.();
      this.setState({ status: 'failed', error: 'timeout' });
    }, TIMEOUT_MS);
    this.stopWaiting = () => {
      unsubscribe(subscription);
      clearTimeout(timeout);
      this.stopWaiting = undefined;
    };

    try {
      await WebsocketManager.instance.waitForWebsocketConnection();
      const response = await SkyMapAxiosServiceFactory.instance
        .createReportServiceInternal()
        .createCadDrawingReport({
          path: { projectId: request.projectId },
          body: { uploadId, fileName: request.fileName, saveResult: request.saveResult },
        });
      exportId = response.data.exportId;
    } catch (error) {
      console.error('Failed to start the report:', error);
      this.stopWaiting();
      const forbidden = isAxiosError(error) && error.response?.status === 403;
      this.setState({ status: 'failed', error: forbidden ? 'access' : 'internal' });
      return;
    }

    this.fileNames.set(exportId, request.fileName);
    this.notified = true;
    this.setState({ status: 'rendering', progress: 0 });
    // Shows the export in the notification center straight away.
    publish(SubscriptionTopic.WebsocketMessageRecieved, {
      message: 'SkyviewReportUpdated',
      exportId,
      status: 'RUNNING',
      progress: 0,
    });

    for (const message of early.filter((x) => x.exportId === exportId)) {
      this.onMessage(message, request.fileName);
    }
  }

  private onMessage(message: SkyviewReportUpdatedMessage, fileName: string) {
    if (!this.isRunning()) {
      return;
    }

    switch (message.status) {
      case 'RUNNING':
        // Messages can come out of order, e.g. the one start publishes for the notifications.
        this.setState({
          status: 'rendering',
          progress: Math.max(
            message.progress,
            this.state.status === 'rendering' ? this.state.progress : 0,
          ),
        });
        break;
      case 'COMPLETED':
        this.stopWaiting?.();
        this.setState({
          status: 'completed',
          fileName,
          downloadUrl: message.downloadUrl,
          resultSaved: message.resultSaved,
          thumbnailUrl: message.thumbnailUrl,
          thumbnailSkipped: message.thumbnailSkipped,
          omitted: this.omitted,
        });
        break;
      case 'FAILED':
        this.stopWaiting?.();
        this.setState({ status: 'failed', error: message.error });
        break;
    }
  }

  private setState(state: ReportExportState) {
    this.state = state;
    this.listeners.forEach((listener) => listener());

    if (state.status === 'failed' && this.detached && !this.notified) {
      publish(SubscriptionTopic.ToastrMessage, {
        message: t('cadDrawingReport.export.failedInBackground', { ns: 'skyviewReport' }),
        type: 'error',
      });
    }
  }
}

export const cadDrawingReportExport = new CadDrawingReportExport();
